import type {
  StockTransfertDetail,
  StockTransfertDetailBarcode,
  StockTransfertDetailLigne,
} from './logiciel-caisse-stock-transfert.service';

export type LcStockTransfertPhaseRef = {
  id: number;
  is_reception: number;
};

const IS_RECEPTION_TERMINEE = 1;

export function lcStockTransfertBarcodeKey(bc: {
  product_id?: number | null;
  code_barre?: string | null;
  couleur?: string | null;
  taille?: string | null;
}): string {
  const pid = bc.product_id != null ? Number(bc.product_id) : 0;
  const code = String(bc.code_barre ?? '').trim();
  const couleur = String(bc.couleur ?? '')
    .trim()
    .toLowerCase();
  const taille = String(bc.taille ?? '')
    .trim()
    .toLowerCase();
  return `${pid}\0${code}\0${couleur}\0${taille}`;
}

/** Clé souple (product + code-barres) si couleur/taille diffèrent entre fiches LC. */
export function lcStockTransfertBarcodeLooseKey(bc: {
  product_id?: number | null;
  code_barre?: string | null;
}): string {
  const pid = bc.product_id != null ? Number(bc.product_id) : 0;
  const code = String(bc.code_barre ?? '').trim();
  return `${pid}\0${code}`;
}

function lookupQtyInMap(
  map: Map<string, number>,
  bc: StockTransfertDetailBarcode
): number | undefined {
  const strict = map.get(lcStockTransfertBarcodeKey(bc));
  if (strict != null) {
    return strict;
  }
  return map.get(lcStockTransfertBarcodeLooseKey(bc));
}

function flattenDetailBarcodes(
  detail: StockTransfertDetail | null | undefined
): Array<{ libelle: string; reference: string; bc: StockTransfertDetailBarcode }> {
  const out: Array<{ libelle: string; reference: string; bc: StockTransfertDetailBarcode }> = [];
  if (!detail) {
    return out;
  }
  for (const lg of detail.lignes || []) {
    const libelle = String(lg.libelle_article ?? '').trim() || 'Article';
    const reference = String(lg.reference ?? '').trim();
    for (const bc of lg.barcodes || []) {
      out.push({ libelle, reference, bc });
    }
  }
  return out;
}

function readEnvoyeQtyFromBarcode(
  bc: StockTransfertDetailBarcode,
  headerIsReception: number
): number {
  if (Number(headerIsReception) === IS_RECEPTION_TERMINEE) {
    return Math.trunc(Number(bc.quantite_envoyee) || 0);
  }
  const q = Math.trunc(Number(bc.quantite) || 0);
  if (bc.quantite_envoyee != null) {
    const env = Math.trunc(Number(bc.quantite_envoyee) || 0);
    if (env > 0) {
      return env;
    }
  }
  return q;
}

function readRecueQtyFromBarcode(
  bc: StockTransfertDetailBarcode,
  headerIsReception: number
): number {
  if (Number(headerIsReception) !== IS_RECEPTION_TERMINEE) {
    return Math.trunc(Number(bc.quantite_recue) || 0);
  }
  if (bc.quantite_recue != null) {
    const rec = Math.trunc(Number(bc.quantite_recue) || 0);
    if (rec > 0) {
      return rec;
    }
  }
  return Math.trunc(Number(bc.quantite) || 0);
}

function buildQtyMap(
  detail: StockTransfertDetail | null | undefined,
  kind: 'envoye' | 'recu'
): Map<string, number> {
  const map = new Map<string, number>();
  if (!detail) {
    return map;
  }
  const headerIs = Number(detail.is_reception);
  for (const { bc } of flattenDetailBarcodes(detail)) {
    const q =
      kind === 'envoye'
        ? readEnvoyeQtyFromBarcode(bc, headerIs)
        : readRecueQtyFromBarcode(bc, headerIs);
    if (q <= 0) {
      continue;
    }
    for (const key of [lcStockTransfertBarcodeKey(bc), lcStockTransfertBarcodeLooseKey(bc)]) {
      map.set(key, Math.max(map.get(key) || 0, q));
    }
  }
  return map;
}

/** Choisit l’id LC pour une phase (priorité : 4 circulation, puis 2 créé, puis 0 défaut). */
export function pickLcTransfertPhaseIdForEnvoye(
  phases: LcStockTransfertPhaseRef[]
): number | null {
  for (const code of [4, 2, 0]) {
    const hit = phases.find((p) => Number(p.is_reception) === code);
    if (hit && Number(hit.id) > 0) {
      return Number(hit.id);
    }
  }
  return null;
}

export function pickLcTransfertPhaseIdForRecue(
  phases: LcStockTransfertPhaseRef[]
): number | null {
  const hit = phases.find((p) => Number(p.is_reception) === IS_RECEPTION_TERMINEE);
  return hit && Number(hit.id) > 0 ? Number(hit.id) : null;
}

export function pickLcTransfertPrimaryPhaseId(
  phases: LcStockTransfertPhaseRef[],
  rowId?: number | null
): number | null {
  if (rowId != null && Number(rowId) > 0) {
    const fromRow = phases.find((p) => Number(p.id) === Number(rowId));
    if (fromRow) {
      return Number(fromRow.id);
    }
  }
  return (
    pickLcTransfertPhaseIdForEnvoye(phases) ??
    pickLcTransfertPhaseIdForRecue(phases) ??
    (phases[0] ? Number(phases[0].id) : null)
  );
}

/**
 * Fusionne QTÉ ENVOYÉE (fiche is_reception 4/2/0) et QTÉ REÇUE (fiche is_reception 1)
 * pour la popup détail transfert entrepôt.
 */
export function mergeLcStockTransfertDetailsForModal(
  primary: StockTransfertDetail,
  envoye: StockTransfertDetail | null | undefined,
  recu: StockTransfertDetail | null | undefined
): StockTransfertDetail {
  const envMap = buildQtyMap(envoye, 'envoye');
  const recMap = buildQtyMap(recu, 'recu');

  const variantByKey = new Map<
    string,
    { libelle: string; reference: string; bc: StockTransfertDetailBarcode }
  >();

  for (const source of [primary, envoye, recu]) {
    for (const { libelle, reference, bc } of flattenDetailBarcodes(source)) {
      const key = lcStockTransfertBarcodeKey(bc);
      if (!variantByKey.has(key)) {
        variantByKey.set(key, {
          libelle,
          reference,
          bc: { ...bc },
        });
      }
    }
  }

  for (const [, entry] of variantByKey) {
    const qEnv = lookupQtyInMap(envMap, entry.bc) ?? 0;
    const qRec = lookupQtyInMap(recMap, entry.bc) ?? 0;
    entry.bc.quantite_envoyee = qEnv;
    entry.bc.quantite_recue = qRec;
    entry.bc.quantite = qEnv > 0 ? qEnv : qRec;
  }

  const ligneMap = new Map<string, StockTransfertDetailLigne>();
  for (const entry of variantByKey.values()) {
    const lgKey = `${entry.libelle}\0${entry.reference}`;
    if (!ligneMap.has(lgKey)) {
      ligneMap.set(lgKey, {
        libelle_article: entry.libelle,
        reference: entry.reference,
        barcodes: [],
      });
    }
    ligneMap.get(lgKey)!.barcodes.push(entry.bc);
  }

  return {
    ...primary,
    pv_ecart_stock_corrected: primary.pv_ecart_stock_corrected === true,
    lignes: [...ligneMap.values()],
  };
}
